import { useState } from 'react';
import { authClient } from '@/lib/auth-client';
import { LinkLogo } from '@/components/nav/link-logo';
import { createFileRoute, Link } from '@tanstack/react-router';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Alert } from '@/components/ui/alert';
import { toast } from 'sonner';
import { getAuthCallbackURL, redirectToAuthTarget } from '@/lib/auth-redirect';

const verifyEmailSearchSchema = z.object({
  email: z.string().email().optional(),
  // Carried from sign-up so a resend keeps an in-progress Claude connection.
  redirect: z.string().optional(),
});

export const Route = createFileRoute('/verify-email')({
  component: VerifyEmail,
  validateSearch: verifyEmailSearchSchema,
  beforeLoad: async ({ context, search }) => {
    // If the user already has a verified session (e.g. they clicked the link
    // in another tab and came back), send them straight to onboarding. Leave
    // unverified / unauthenticated visitors on this page.
    const user = context.session?.user as { emailVerified?: boolean } | undefined;
    if (context.isAuthenticated() && user?.emailVerified) {
      redirectToAuthTarget(search.redirect ?? '/onboarding');
    }
  },
});

function VerifyEmail() {
  const { email, redirect: redirectTo } = Route.useSearch();
  const [isResending, setIsResending] = useState(false);

  const handleResend = async () => {
    if (!email) {
      toast.error('Missing email', {
        description: 'Go back to sign up and try again.',
      });
      return;
    }
    setIsResending(true);
    const { error } = await authClient.sendVerificationEmail({
      email,
      callbackURL: getAuthCallbackURL(redirectTo, '/onboarding'),
    });
    setIsResending(false);
    if (error) {
      if (error.status === 429) {
        toast.error('Please wait a moment', {
          description: 'You can only request a new verification email every so often.',
        });
      } else {
        toast.error('Failed to resend', {
          description: error.message || 'Please try again in a minute.',
        });
      }
      return;
    }
    toast.success('Verification email sent', {
      description: `We sent a new link to ${email}.`,
    });
  };

  return (
    <section className="py-12 sm:py-16 lg:py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-sm">
          <div className="flex flex-col items-center">
            <LinkLogo />
            <h1 className="mt-12 text-3xl font-bold">Check Your Email</h1>
            <p className="text-muted-foreground mt-4 text-center font-medium">
              {email ? (
                <>
                  We sent a verification link to{' '}
                  <span className="text-foreground">{email}</span>. Click it to
                  continue setting up your account.
                </>
              ) : (
                'We sent you a verification link. Click it to continue setting up your account.'
              )}
            </p>
          </div>

          <div className="mt-12 space-y-4">
            <Alert
              title="Waiting for Verification"
              subTitle="Once you click the link in your email, you'll be signed in automatically."
            />
            <Button
              className="w-full"
              size="lg"
              variant="outline"
              onClick={handleResend}
              disabled={isResending || !email}
            >
              {isResending ? 'Sending...' : 'Resend Verification Email'}
            </Button>
          </div>

          <div className="mt-6 space-y-2 text-center">
            <p className="text-muted-foreground font-medium">
              Wrong email?{' '}
              <Link to="/sign-up" search={{ redirect: redirectTo }}>
                <Button className="px-1" variant="link">
                  Sign Up Again
                </Button>
              </Link>
            </p>
            <p className="text-muted-foreground font-medium">
              Already verified?{' '}
              <Link to="/sign-in" search={{ redirect: redirectTo }}>
                <Button className="px-1" variant="link">
                  Sign In
                </Button>
              </Link>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
